import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AnalysisResult, AnalysisSetup, Candle, ChartMarker, Timeframe, AnalysisMethodology, AIModel, MarketProvider } from '@trading/shared';
import { DEFAULT_AI_MODEL } from '@trading/shared';
import { normalizeDirection, normalizeMethodology, normalizeSetup, normalizeMarkers } from './analysisNormalizers.js';

/**
 * Shape of an analysis record as returned directly from the database / API.
 *
 * The DB schema stores several fields in a flat form (legacy) that the
 * `AnalysisResult` type does not model. Additionally:
 * - `direction` comes from the DB as `'LONG' | 'SHORT'` (Prisma enum casing).
 * - `methodology` includes `'price_action'` (DB enum value) instead of
 *   `'price-action'` (shared type convention).
 * - `markers` is the raw DB column; the shared type surfaces it as
 *   `chartMarkers` after the API response mapping.
 *
 * `loadAnalysis` receives this shape and normalises it into `AnalysisResult`
 * before storing it in `currentAnalysis`.
 */
type StoredAnalysisPayload = Omit<AnalysisResult, 'direction' | 'methodology' | 'chartMarkers'> & {
  /** DB enum casing — normalised to lowercase before storing. */
  direction?: 'LONG' | 'SHORT' | 'long' | 'short' | null;
  /** DB enum value for price-action methodology. */
  methodology: AnalysisMethodology | 'price_action';
  /** Legacy flat targets field (pre-setup era). */
  targets?: AnalysisSetup['targets'] | null;
  /** Legacy flat risk-percent field. */
  riskPercent?: number | null;
  /** Raw DB column name — mapped to `chartMarkers` by the store. */
  markers?: ChartMarker[] | null;
  /** Present on API response after `liftMethodologyOverlay` mapping. */
  chartMarkers?: ChartMarker[] | null;
};

export type { StoredAnalysisPayload };

interface AnalysisState {
  // Current analysis settings
  symbol: string;
  timeframe: Timeframe;
  methodology: AnalysisMethodology;
  aiModel: AIModel;
  candles: Candle[];
  /** Selected custom prompt id (Pro+). `null` = use the official template only. */
  selectedUserPromptId: string | null;
  /** Forced data provider for debugging. `null` = auto-routing (default). */
  debugProvider: MarketProvider | null;

  // Analysis state
  currentAnalysis: AnalysisResult | null;
  isAnalyzing: boolean;
  analysisProgress: number;
  error: string | null;

  // History
  analysisHistory: AnalysisResult[];
  historyLoading: boolean;

  // Actions
  setSymbol: (symbol: string) => void;
  setTimeframe: (timeframe: Timeframe) => void;
  setMethodology: (methodology: AnalysisMethodology) => void;
  setAIModel: (aiModel: AIModel) => void;
  setCandles: (candles: Candle[]) => void;
  setSelectedUserPromptId: (id: string | null) => void;
  setDebugProvider: (provider: MarketProvider | null) => void;
  startAnalysis: () => void;
  setAnalysisProgress: (progress: number) => void;
  setAnalysisResult: (result: AnalysisResult) => void;
  setAnalysisError: (error: string) => void;
  clearAnalysis: () => void;
  setHistory: (history: AnalysisResult[]) => void;
  setHistoryLoading: (loading: boolean) => void;
  loadAnalysis: (analysis: StoredAnalysisPayload) => void;
  setCurrentAnalysis: (analysis: AnalysisResult) => void;
}

export const useAnalysisStore = create<AnalysisState>()(
  persist(
    (set) => ({
      // Initial state
      symbol: 'frxXAUUSD',
      timeframe: '15m',
      methodology: 'smc',
      aiModel: DEFAULT_AI_MODEL,
      candles: [],
      selectedUserPromptId: null,
      debugProvider: null,
      currentAnalysis: null,
      isAnalyzing: false,
      analysisProgress: 0,
      error: null,
      analysisHistory: [],
      historyLoading: false,

      // Actions
      setSymbol: (symbol) => set({ symbol }),
      setTimeframe: (timeframe) => set({ timeframe }),
      setMethodology: (methodology) => set({ methodology }),
      setAIModel: (aiModel) => set({ aiModel }),
      setCandles: (candles) => set({ candles }),
      setSelectedUserPromptId: (selectedUserPromptId) => set({ selectedUserPromptId }),
      setDebugProvider: (debugProvider) => set({ debugProvider }),

  startAnalysis: () =>
    set({
      isAnalyzing: true,
      analysisProgress: 0,
      error: null,
      currentAnalysis: null,
    }),

  setAnalysisProgress: (progress) => set({ analysisProgress: progress }),

  setAnalysisResult: (result) =>
    set({
      currentAnalysis: result,
      isAnalyzing: false,
      analysisProgress: 100,
    }),

  setAnalysisError: (error) =>
    set({
      error,
      isAnalyzing: false,
      analysisProgress: 0,
    }),

  clearAnalysis: () =>
    set({
      currentAnalysis: null,
      error: null,
      analysisProgress: 0,
    }),

  setHistory: (history) => set({ analysisHistory: history }),
  setHistoryLoading: (loading) => set({ historyLoading: loading }),
  loadAnalysis: (analysis) => {
    // Destructure out fields that differ between StoredAnalysisPayload and
    // AnalysisResult so we can override them with normalised values below.
    const {
      direction: _direction,
      methodology: _methodology,
      chartMarkers: _chartMarkers,
      markers: _markers,
      targets: _targets,
      riskPercent: _riskPercent,
      ...rest
    } = analysis;

    const methodology = normalizeMethodology(analysis.methodology);

    const normalizedAnalysis: AnalysisResult = {
      ...rest,
      direction: normalizeDirection(analysis.direction),
      methodology,
      setup: normalizeSetup(analysis),
      chartMarkers: normalizeMarkers(analysis),
    };

    return set({
      currentAnalysis: normalizedAnalysis,
      symbol: analysis.symbol,
      timeframe: analysis.timeframe,
      methodology,
      isAnalyzing: false,
      error: null,
    });
  },
  setCurrentAnalysis: (analysis) => set({ currentAnalysis: analysis }),
    }),
    {
      name: 'trading-preferences',
      version: 3,
      partialize: (state) => ({
        symbol: state.symbol,
        timeframe: state.timeframe,
        methodology: state.methodology,
        aiModel: state.aiModel,
        selectedUserPromptId: state.selectedUserPromptId,
        debugProvider: state.debugProvider,
      }),
      // Reset stale `aiModel` values (e.g. old "gemini-3-flash-preview" from
      // a previous build) so a hydrated localStorage cannot send an enum
      // value the API now rejects with VAL_001.
      migrate: (persisted: unknown) => {
        const validModels: readonly AIModel[] = [
          'gemini-2.5-flash',
          'gemini-2.5-pro',
          'openai/gpt-4o',
          'openai/gpt-4o-mini',
          'anthropic/claude-sonnet-4',
          'deepseek/deepseek-chat',
          'google/gemini-2.5-flash',
        ];
        if (persisted && typeof persisted === 'object') {
          const obj = persisted as { aiModel?: unknown; debugProvider?: unknown };
          let next = persisted;
          if (typeof obj.aiModel !== 'string' || !validModels.includes(obj.aiModel as AIModel)) {
            next = { ...next, aiModel: DEFAULT_AI_MODEL };
          }
          const validProviders: readonly (MarketProvider | null)[] = [null, 'deriv', 'binance', 'twelvedata'];
          if (!validProviders.includes(obj.debugProvider as MarketProvider | null)) {
            next = { ...next, debugProvider: null };
          }
          return next;
        }
        return persisted;
      },
    }
  )
);
